'use client';

import { Fragment, useId } from 'react';
import { Switch } from '@1debit/chimekit';
import { CALCULATOR_RESULT_CARD } from 'components/Widget/shared/cardStyles';
import { CalculatorEnrollmentCta } from 'components/Widget/shared/CalculatorEnrollmentCta';
import { formatCompactCurrency, formatCurrency, toCents } from 'components/Widget/shared/currency';
import { cn } from 'utils/styles';
import {
  CURRENT_PLAN_HEADING,
  EXTRA_PAYMENT_FIELD,
  formatDuration,
  COMPARISON_ROWS,
  MIN_TEASER_TERM_MONTHS,
  RESULT_COPY,
  WITH_EXTRA_PAYMENT_HEADING,
} from './constants';
import { LoanBalanceLineGraph } from './LoanBalanceLineGraph';
import type { LoanPanel, LoanScenario } from './types';

interface LoanCalculatorResultProps {
  panel: LoanPanel;
  showComparison: boolean;
  onToggleComparison: (value: boolean) => void;
}

const RAISED = 'align-super text-heading-sm';

const LABEL = 'text-label-serif text-content-secondary';

const SAVINGS_CARD = 'rounded-lg bg-accent-green-200 px-4 py-3';

/** Avoid `<sup>` because global styles and assistive technology treat it as a footnote marker. */
const HeroAmount = ({ value }: { value: number }) => {
  const cents = toCents(value);
  const dollars = Math.trunc(cents / 100).toLocaleString('en-US');

  return (
    <p className="text-heading-lg">
      <span className="sr-only">{formatCurrency(value)}</span>
      <span aria-hidden>
        <span className={RAISED}>$</span>
        {dollars}.<span className={RAISED}>{String(cents % 100).padStart(2, '0')}</span>
      </span>
    </p>
  );
};

interface ScenarioValueProps {
  scenario: LoanScenario;
  row: (typeof COMPARISON_ROWS)[number];
}

/** Compact on screen, exact to a screen reader: `$183K` alone is not a figure to act on. */
const ScenarioValue = ({ scenario, row }: ScenarioValueProps) =>
  row.key === 'years' ? (
    <>{formatDuration(scenario.monthsToPayoff)}</>
  ) : (
    <>
      <span className="sr-only">{formatCurrency(scenario[row.key])}</span>
      <span aria-hidden>{formatCompactCurrency(scenario[row.key])}</span>
    </>
  );

export const LoanCalculatorResult = ({ panel, showComparison, onToggleComparison }: LoanCalculatorResultProps) => {
  const switchId = useId();
  const { result } = panel;
  const extraPayment = formatCurrency(result.extraPayment);
  const interestSaved = formatCurrency(result.interestSaved);
  const showExtraPayment = result.isExtraPaymentFromUser || result.termMonths >= MIN_TEASER_TERM_MONTHS;
  /** The toggle outlives the panel it was flipped on, so a hidden switch must not leave its columns behind. */
  const isComparing = showComparison && showExtraPayment;

  return (
    <div className={cn(CALCULATOR_RESULT_CARD, 'gap-5')}>
      <div>
        <p className={LABEL}>{RESULT_COPY.heroEyebrow}</p>
        <HeroAmount value={result.baseline.monthlyPayment} />
      </div>

      <table className="w-full border-collapse text-left">
        <thead>
          <tr className={cn(LABEL, 'text-left')}>
            <th scope="col">
              <span className="sr-only">Figure</span>
            </th>
            <th scope="col" className="px-2 py-2 text-center font-normal">
              {CURRENT_PLAN_HEADING}
            </th>
            {isComparing && (
              <>
                <td aria-hidden />
                <th scope="col" className="py-2 text-center font-normal">
                  {WITH_EXTRA_PAYMENT_HEADING}
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {COMPARISON_ROWS.map(row => (
            <tr key={row.key} className="border-t border-border-primary">
              <th scope="row" className={cn(LABEL, 'py-3 pr-3 font-normal')}>
                {row.label}
              </th>
              <td className="px-2 py-3 text-center text-heading-sm whitespace-nowrap">
                <ScenarioValue scenario={result.baseline} row={row} />
              </td>
              {isComparing && (
                <>
                  {/* The arrow is the comparison's only visual direction; a reader gets the column header. */}
                  <td aria-hidden className="text-content-secondary">
                    &rarr;
                  </td>
                  <td className="py-3 pl-2 text-center text-heading-sm whitespace-nowrap">
                    <ScenarioValue scenario={result.withExtra} row={row} />
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {showExtraPayment && (
        <>
          <div className="flex items-center gap-3">
            <Switch
              id={switchId}
              value={isComparing}
              onValueChange={onToggleComparison}
              aria-labelledby={`${switchId}-label`}
            />
            <label id={`${switchId}-label`} htmlFor={switchId} className="text-label-serif">
              {EXTRA_PAYMENT_FIELD.label} (optional) - {RESULT_COPY.comparisonToggleAmount(extraPayment)}
            </label>
          </div>

          <p className={`${SAVINGS_CARD} ${LABEL}`}>
            {(isComparing
              ? RESULT_COPY.comparison(extraPayment, interestSaved, result.monthsSaved)
              : RESULT_COPY.teaser(extraPayment, interestSaved, result.monthsSaved)
            ).map((segment, index) =>
              segment.isEmphasized ? (
                <span key={index} className="font-bold">
                  {segment.text}
                </span>
              ) : (
                <Fragment key={index}>{segment.text}</Fragment>
              ),
            )}
          </p>
        </>
      )}

      <LoanBalanceLineGraph result={result} showComparison={isComparing} />

      <CalculatorEnrollmentCta />
    </div>
  );
};
